import { useMemo } from 'react';

import { QRCodeCanvas } from 'qrcode.react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
    Box,
    Button,
    Card,
    colors,
    Divider,
    ElvaLogoName,
    Inline,
    Input,
    Page,
    PasswordInput,
    Stack,
    u,
    Spinner,
    CommitSha,
} from '@ui';

import { QR_KEY, QR_URL } from '../../consts';
import { useBoothQRCode, useBoothSession, useBoothUserLogin, useUser } from '../../hooks';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen';

import * as css from './BoothUserLoginScreen.css';

type UserLoginScreenProps = {
    registerPath: string;
    resetPasswordPath: string;
};

export function UserLoginScreen({ registerPath, resetPasswordPath }: UserLoginScreenProps) {
    const { register, handleSubmit } = useForm<Record<'email' | 'password', string>>();
    const { isError, error } = useUser();
    const login = useBoothUserLogin();
    const navigate = useNavigate();

    const { data: code } = useBoothQRCode();

    const encodedURL = useMemo(() => {
        if (!code?.qrCode) return null;
        const baseURL = new URL(QR_URL);
        baseURL.searchParams.set(QR_KEY, code?.qrCode);
        return baseURL.toString();
    }, [code?.qrCode]);

    return (
        <Page>
            <Card>
                <Stack gap={u(2.5)}>
                    <Inline alignItems="center" justifyContent="space-between">
                        <Stack gap={u(0.5)}>
                            <h4>Login</h4>
                            <p
                                style={{
                                    fontSize: '0.8em',
                                    color: colors.gray[400],
                                }}
                            >
                                Log in with your email
                            </p>
                        </Stack>
                        <ElvaLogoName width={90} />
                    </Inline>
                    <Divider />
                    <Box display="flex" gap={u(3)}>
                        <Box p={u(2)}>
                            {encodedURL ? (
                                <QRCodeCanvas size={180} value={encodedURL} />
                            ) : (
                                <Box
                                    style={{ width: 180, height: 180 }}
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                >
                                    <Spinner size="l" />
                                </Box>
                            )}
                        </Box>
                        <form onSubmit={handleSubmit((data) => login.mutateAsync(data))}>
                            <Stack gap={u(2.5)} className={css.inputsContainer}>
                                {(isError || login.error) && (
                                    <span className={css.errorMessage}>
                                        Login failed: {error?.message ?? login.error?.message}
                                    </span>
                                )}
                                <Input
                                    {...register('email')}
                                    required
                                    type="email"
                                    label="Email Address"
                                    placeholder="you@example.com"
                                />
                                <PasswordInput
                                    {...register('password')}
                                    required
                                    label="Password"
                                    placeholder="Enter your password"
                                />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <button
                                        type="button"
                                        className={css.register}
                                        onClick={() => navigate(registerPath)}
                                    >
                                        Register
                                    </button>
                                    <button
                                        type="button"
                                        className={css.forgotLink}
                                        style={{ height: '100%' }}
                                        onClick={() => navigate(resetPasswordPath)}
                                    >
                                        Forgot password?
                                    </button>
                                </Box>
                                <Button
                                    type="submit"
                                    isDisabled={login.isPending}
                                    variant="primary"
                                    isLoading={login.isPending}
                                >
                                    Sign in
                                </Button>
                            </Stack>
                        </form>
                    </Box>
                    <CommitSha />
                </Stack>
            </Card>
        </Page>
    );
}

export function BoothUserLoginScreen(props: UserLoginScreenProps) {
    const { data: boothSession, isLoading } = useBoothSession();
    const { data: user } = useUser();

    if (!boothSession || isLoading || (!!boothSession.userId && !user)) {
        return <LoadingScreen message="Loading Booth Session..." />;
    }
    return <UserLoginScreen {...props} />;
}
